import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Clock, MapPin, Users, Phone, Mail, CheckCircle2, ArrowUp, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

interface EsperaItem {
  id: number;
  nombreCliente: string;
  telefono?: string;
  email?: string;
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  zonaId?: number;
  zonaNombre?: string;
  sucursalId?: number;
  esperaPosicion?: number;
  esperaEntradaAt?: string;
  notificadaEsperaAt?: string;
}

function fmtFecha(s?: string) {
  if (!s) return "—";
  try {
    return new Date(s + "T12:00:00").toLocaleDateString("es-VE", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return s; }
}

function tiempoEspera(iso?: string) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${min % 60}m`;
  return `${Math.floor(h / 24)}d`;
}

export function ListaEsperaPage() {
  const qc = useQueryClient();
  const [filtroFecha, setFiltroFecha] = useState<string>("");
  const [horasInput, setHorasInput] = useState<string>("");

  const config = useQuery({
    queryKey: ["lista-espera-config"],
    queryFn: () => api<{ horasConfirmar: number }>(`/v1/reservas/lista-espera/config`),
  });

  const guardarConfig = useMutation({
    mutationFn: (horas: number) =>
      api(`/v1/reservas/lista-espera/config`, { method: "POST", body: { horasConfirmar: horas } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lista-espera-config"] }),
  });

  const horasConfirmar = horasInput !== "" ? horasInput : String(config.data?.horasConfirmar ?? "");

  const lista = useQuery({
    queryKey: ["lista-espera", filtroFecha],
    queryFn: () => {
      const q = new URLSearchParams();
      if (filtroFecha) q.set("fecha", filtroFecha);
      return api<EsperaItem[]>(`/v1/reservas/lista-espera?${q.toString()}`);
    },
    refetchInterval: 30_000,
  });

  const promover = useMutation({
    mutationFn: (id: number) => api(`/v1/reservas/lista-espera/${id}/promover`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lista-espera"] }),
  });

  // Agrupar por fecha+hora+zona para visualización
  const items = lista.data ?? [];
  const agrupado = items.reduce<Record<string, EsperaItem[]>>((acc, r) => {
    const k = `${r.fechaReserva} ${r.horaReserva.slice(0, 5)} · ${r.zonaNombre ?? "Sin zona"}`;
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-bold text-ink-900 leading-tight flex items-center gap-3">
          <Clock className="size-7 text-amber-500" />
          Lista de espera
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Reservas que no entraron por capacidad. Cuando alguien cancela, se promueven automáticamente por orden de llegada.
        </p>
      </header>

      <div className="card p-4 mb-4">
        <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-1">
          Plazo para confirmar tras ser promovido
        </p>
        <p className="text-xs text-ink-400 mb-3">
          Si el cliente promovido no confirma dentro de este plazo, su cupo vuelve a liberarse y pasa al siguiente en espera.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number" min={1} max={168}
            value={horasConfirmar}
            onChange={(e) => setHorasInput(e.target.value)}
            disabled={config.isLoading}
            className="input w-24 text-center font-bold"
          />
          <span className="text-sm text-ink-500">horas</span>
          <button
            onClick={() => {
              const h = Number(horasInput || config.data?.horasConfirmar || 4);
              guardarConfig.mutate(h, { onSuccess: () => setHorasInput("") });
            }}
            disabled={guardarConfig.isPending || horasInput === ""}
            className="ml-2 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-semibold hover:bg-brand-700 disabled:opacity-50"
          >
            {guardarConfig.isPending ? "Guardando…" : "Guardar"}
          </button>
          {guardarConfig.isSuccess && horasInput === "" && (
            <span className="text-xs text-emerald-600">Guardado</span>
          )}
        </div>
      </div>

      <div className="card p-4 mb-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wide mb-1">
            Filtrar por fecha
          </label>
          <input type="date" value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
            className="input" />
        </div>
        <div className="flex-1 text-right text-sm text-ink-500">
          Total: <b className="text-ink-800">{items.length}</b> en espera
        </div>
      </div>

      {lista.isLoading ? (
        <div className="text-center py-12 text-ink-400">
          <Loader2 className="size-6 animate-spin mx-auto mb-2" />
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-6xl mb-3">🎉</p>
          <p className="font-medium text-ink-800 text-lg">¡Sin lista de espera!</p>
          <p className="text-sm text-ink-500 mt-1">
            Todas las reservas que recibiste cabían en la capacidad configurada.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(agrupado).map(([clave, grupo]) => (
            <section key={clave} className="card overflow-hidden">
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
                <Clock className="size-4 text-amber-700" />
                <h3 className="font-semibold text-amber-900 text-sm">{clave}</h3>
                <span className="ml-auto text-xs text-amber-700">
                  {grupo.length} en espera
                </span>
              </div>
              <div className="divide-y divide-ink-100">
                {grupo
                  .sort((a, b) => (a.esperaPosicion ?? 999) - (b.esperaPosicion ?? 999))
                  .map((r) => (
                    <div key={r.id} className="p-4 flex items-center gap-3">
                      <div className="size-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold shrink-0">
                        #{r.esperaPosicion ?? "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-ink-800">
                          {r.nombreCliente}
                          <span className="ml-2 text-xs text-ink-500 inline-flex items-center gap-1">
                            <Users className="size-3" /> {r.numPersonas}
                          </span>
                        </p>
                        <div className="flex flex-wrap gap-3 text-xs text-ink-500 mt-1">
                          {r.telefono && (
                            <a href={`tel:${r.telefono}`} className="hover:text-ink-800 inline-flex items-center gap-1">
                              <Phone className="size-3" /> {r.telefono}
                            </a>
                          )}
                          {r.email && (
                            <a href={`mailto:${r.email}`} className="hover:text-ink-800 inline-flex items-center gap-1 truncate max-w-[200px]">
                              <Mail className="size-3" /> {r.email}
                            </a>
                          )}
                          <span className="inline-flex items-center gap-1 text-ink-400">
                            <Clock className="size-3" /> hace {tiempoEspera(r.esperaEntradaAt)}
                          </span>
                          {r.notificadaEsperaAt && (
                            <span className="text-emerald-600 inline-flex items-center gap-1">
                              <CheckCircle2 className="size-3" /> notificado
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <Link to={`/reservas/${r.id}`}
                          className="px-3 py-1.5 rounded-lg border border-ink-200 hover:border-ink-400 text-xs text-ink-700 transition">
                          Ver
                        </Link>
                        <button
                          onClick={() => promover.mutate(r.id)}
                          disabled={promover.isPending}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                          <ArrowUp className="size-3" />
                          Promover
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
